import { Seo } from '@/components/seo/Seo';
import { Container } from '@/components/ui/Container';
import { aboutConfig } from '@/data/site';

const PersonalAbout = () => (
  <>
    <Seo title="About" description={aboutConfig.seoDescription} path="/about" />

    <Container>
      <section className="section-stack prose-page">

        <div className="section-heading">
          <h1>{aboutConfig.heading}</h1>
        </div>

        <div className="space-y-4">
          <p className="text-lg font-medium">
            Most organisations don't fail because of poor engineering.
            They fail because they are exceptionally good at building the wrong things.
          </p>
          <p>I focus on solving that problem.</p>
        </div>

        <div className="mt-8 space-y-4">
          <h2>The Shift: CTO → CITO</h2>
          <p>
            My work sits at the intersection of business strategy, technology, and
            transformation—ensuring organisations don't just deliver software, but deliver
            the right outcomes.
          </p>
          <p>
            A traditional CTO ensures that technology is built well.
            A CITO ensures that the right things are built at all.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <h2>The Framework</h2>
          <p className="font-semibold text-center">
            Value → Strategy → Capability → Technology → Delivery
          </p>
          <p>
            This model ensures that every investment, backlog item, and architectural
            decision is aligned to a clear business outcome.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <h2>What I Do</h2>
          <ul>
            <li>Define and prioritise business capabilities</li>
            <li>Align technology strategy to business outcomes</li>
            <li>Fix broken Agile and SAFe implementations</li>
            <li>Introduce capability-driven investment models</li>
            <li>Reduce waste and improve strategic clarity</li>
          </ul>
        </div>

        <div className="mt-8 space-y-4">
          <h2>The Reality I See</h2>
          <p>Across organisations, the same patterns appear:</p>
          <ul>
            <li>Misaligned investment</li>
            <li>Disconnected backlogs</li>
            <li>Reactive architecture</li>
          </ul>
          <p>The solution is not more process, tools, or frameworks.</p>
          <p className="font-medium">The solution is clarity.</p>
          <p className="font-medium">Clarity of value. Clarity of capability. Clarity of intent.</p>
        </div>

        <div className="mt-10">
          <p className="text-lg font-semibold">That is the role I operate in.</p>
        </div>

      </section>
    </Container>
  </>
);

const PublishWeaverAbout = () => (
  <>
    <Seo title="About" description={aboutConfig.seoDescription} path="/about" />

    <Container>
      <section className="section-stack prose-page">

        <div className="section-heading">
          <h1>{aboutConfig.heading}</h1>
        </div>

        <div className="space-y-4">
          <p className="text-lg font-medium">
            Most content platforms optimise for volume. PublishWeaver optimises for integrity.
          </p>
          <p>
            This blog documents the thinking behind a platform built for organisations that
            need governed, structured content at scale.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <h2>What We Cover</h2>
          <ul>
            <li>Governed publishing and editorial operations</li>
            <li>Canonical content architecture</li>
            <li>Capability-driven content strategy</li>
            <li>Platform updates and product thinking</li>
          </ul>
        </div>

        <div className="mt-8 space-y-4">
          <h2>The Problem We Solve</h2>
          <p>
            Large organisations accumulate content debt the same way they accumulate technical
            debt—quietly, until it becomes expensive. Duplicate content, broken governance,
            and disconnected workflows are the norm, not the exception.
          </p>
          <p>
            PublishWeaver gives editorial and technology teams a single, structured system
            for managing content with confidence.
          </p>
        </div>

        <div className="mt-10">
          <p className="text-lg font-semibold">
            Written by the team building PublishWeaver.
          </p>
        </div>

      </section>
    </Container>
  </>
);

export const AboutPage = () => {
  const tenant = import.meta.env.VITE_TENANT;
  return tenant === 'personal' ? <PersonalAbout /> : <PublishWeaverAbout />;
};
